import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ArticleListItem } from "@/lib/api";
import { channelNameOf, isInternalNote } from "@/lib/articleChannel";
import { Button } from "@/components/ui/Button";
import { Menu, MenuItem } from "@/components/ui/Menu";
import { useReplyDraft } from "@/lib/replyDrafts";
import { requestComposer, requestConversationView } from "./telegram/composerBus";
import { ReplyDialog } from "./ReplyDialog";
import { isChatLive, useTicketChat } from "@/lib/webchatApi";
import { BounceDialog, ForwardDialog, SplitDialog } from "./ArticleActionDialogs";
import { useDeleteArticleNote } from "./useDeleteArticleNote";

/**
 * Reply / reply-all / forward as buttons; the less-common bounce/split
 * actions live under a ⋮ menu (known Menu clipping bug rules out nesting a
 * second flyout, so this stays single-level). Same dialogs as the former
 * per-article `ArticleActions` row — shared by the split view's reading
 * pane and the conversation view's per-bubble hover action island.
 */
export function ArticleQuickActions({
  ticketId,
  article,
  canNote,
  canDelete = false,
  replyTestId,
  compact = false,
}: {
  ticketId: number;
  article: ArticleListItem;
  canNote: boolean;
  /** Whether the agent may delete this article (``rw`` permission). Delete
   * is only ever offered for internal, non-customer-visible notes. */
  canDelete?: boolean;
  /** Testid for the primary reply button (split pane needs a stable one). */
  replyTestId?: string;
  /** Icon-only buttons for the conversation view's hover island. */
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<"reply" | "replyAll" | "forward" | "bounce" | "split" | null>(
    null,
  );
  const isTelegram = channelNameOf(article) === "Telegram";
  // A live web chat answers in the messenger composer like Telegram; an
  // ended one falls back to the e-mail dialog (no reply-all there either).
  const isWebchat = channelNameOf(article) === "WebChat";
  const chatQ = useTicketChat(ticketId, isWebchat);
  const webchatLive = isChatLive(chatQ.data);
  const messengerReply = isTelegram || (isWebchat && webchatLive);
  // Until the chat state is known the right reply surface is not: wait instead of
  // opening the e-mail dialog on what may be a live chat.
  const chatLoading = isWebchat && chatQ.isPending;
  // The backend knows our system addresses: false = reply-all would reach
  // nobody beyond the plain reply, so it is offered greyed out.
  const replyAllUseful = article.reply_all_available !== false;
  const noPerm = t("ticket.toolbar.noPermission");
  const showDelete = canDelete && isInternalNote(article);
  const del = useDeleteArticleNote(ticketId, article.id);
  // An unsent draft turns the reply button into a "continue" affordance —
  // the compact (icon-only) variant keeps just the dot, there is no room
  // for the longer label in the conversation view's hover island.
  const hasDraft = Boolean(useReplyDraft(ticketId, article.id));

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`article-actions-${article.id}`}>
      <span title={!canNote ? noPerm : undefined} className="inline-flex">
        <Button
          size="sm"
          variant="primary"
          disabled={!canNote || chatLoading}
          data-testid={replyTestId}
          data-has-draft={hasDraft ? "true" : undefined}
          title={compact ? (hasDraft ? t("ticket.draftResume") : t("ticket.reply")) : undefined}
          onClick={() => {
            if (messengerReply) {
              // May be reached from the split view (a Telegram ticket the
              // agent manually switched away from conversation) — bring the
              // composer's view back first, like TicketHeaderActions; the
              // request is buffered until it mounts there.
              requestConversationView(ticketId);
              requestComposer(ticketId, { quoteArticleId: article.id, focus: true });
            } else {
              setDialog("reply");
            }
          }}
        >
          ↩ {!compact && (hasDraft ? t("ticket.draftResume") : t("ticket.reply"))}
          {hasDraft && (
            <span
              aria-hidden
              data-testid={`article-draft-dot-${article.id}`}
              className="h-1.5 w-1.5 shrink-0 rounded-full bg-current"
            />
          )}
        </Button>
      </span>
      {!isTelegram && !isWebchat && !compact && (
        <span
          title={!canNote ? noPerm : !replyAllUseful ? t("ticket.replyAllNoOthers") : undefined}
          className="inline-flex"
        >
          <Button
            size="sm"
            variant="secondary"
            disabled={!canNote || !replyAllUseful}
            data-testid={`article-reply-all-${article.id}`}
            onClick={() => setDialog("replyAll")}
          >
            ↪ {t("ticket.replyAll")}
          </Button>
        </span>
      )}
      <Button
        size="sm"
        variant="secondary"
        title={compact ? t("ticket.forward") : undefined}
        onClick={() => setDialog("forward")}
      >
        → {!compact && t("ticket.forward")}
      </Button>
      <Menu
        align="right"
        trigger={({ ref, toggleProps }) => (
          <button
            ref={ref}
            type="button"
            aria-label={t("ticket.moreActions")}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-hairline text-muted hover:bg-surface-subtle"
            {...toggleProps}
          >
            ⋮
          </button>
        )}
      >
        <MenuItem onSelect={() => setDialog("bounce")}>{t("ticket.bounce")}</MenuItem>
        <MenuItem onSelect={() => setDialog("split")}>{t("ticket.splitAction")}</MenuItem>
        {showDelete && (
          <MenuItem
            danger
            testId={`article-delete-${article.id}`}
            onSelect={() => void del.requestDelete()}
          >
            {t("ticket.deleteNote")}
          </MenuItem>
        )}
      </Menu>
      {del.errorMessage && (
        <p className="text-xs text-danger" data-testid={`article-delete-error-${article.id}`}>
          {del.errorMessage}
        </p>
      )}
      {showDelete && del.dialog}

      {canNote && (
        <ReplyDialog
          ticketId={ticketId}
          articleId={article.id}
          replyAll={dialog === "replyAll"}
          open={dialog === "reply" || dialog === "replyAll"}
          onClose={() => setDialog(null)}
          channelName={channelNameOf(article)}
        />
      )}
      <ForwardDialog
        ticketId={ticketId}
        articleId={article.id}
        open={dialog === "forward"}
        onClose={() => setDialog(null)}
      />
      <BounceDialog
        ticketId={ticketId}
        articleId={article.id}
        open={dialog === "bounce"}
        onClose={() => setDialog(null)}
      />
      <SplitDialog
        ticketId={ticketId}
        articleId={article.id}
        open={dialog === "split"}
        onClose={() => setDialog(null)}
      />
    </div>
  );
}
